import { tr } from '@/lib/i18n';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isStandaloneMode } from '@/utils/platform';
import { logger } from '@/lib/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const LOADING_SUPPORT_DELAY_MS = 6000;

// Grace period before treating "no user" as "logged out".
//
// A session can be absent for a moment even when the person IS signed in:
// an expired access token being refreshed in the background, or a PWA cold
// start restoring its saved session. Redirecting on that first frame bounces
// a legitimate user to /auth, and when the session lands they get moved
// again, which reads to them as the app looping. Waiting a beat costs a real
// visitor a short spinner and costs a signed-in parent nothing.
const AUTH_SETTLE_GRACE_MS = 1500;

function ProtectedRouteLoadingState({ showSupportFallback }: { showSupportFallback: boolean }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md text-center space-y-3">
        {/* 25/09: o ícone do app no lugar do cérebro rosa de emoji (e sem pulso infinito). */}
        <img src="/brand/app-icon-192.png" alt="" width={72} height={72} className="mx-auto h-18 w-18 rounded-2xl shadow-md" style={{ width: 72, height: 72 }} />
        <p className="text-xl font-semibold text-foreground">{tr("Loading your profile...")}</p>
        {/* "We're validating your data to unlock access securely" saiu em 20/09:
            "validating" e "unlock" não dizem nada para quem cuida da criança e
            não usa aplicativo nenhum, e esta é a porta de TODAS as telas. */}
        <p className="text-[15px] leading-relaxed text-muted-foreground">{tr("This only takes a moment.")}</p>
        {showSupportFallback ? (
          <p className="text-[15px] leading-relaxed text-muted-foreground" role="status">{tr("If this takes longer than usual, try again in a moment or contact support.")}</p>
        ) : null}
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [showSupportFallback, setShowSupportFallback] = useState(false);
  const [authSettled, setAuthSettled] = useState(false);
  const shouldShowLoadingState = loading || (!!user && user.quiz_completed === undefined);

  // Only conclude "logged out" after the grace period above has passed with
  // still no user. Any user or loading state resets it.
  useEffect(() => {
    if (loading || user) {
      setAuthSettled(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setAuthSettled(true);
    }, AUTH_SETTLE_GRACE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loading, user]);

  useEffect(() => {
    if (!shouldShowLoadingState) {
      setShowSupportFallback(false);
      return;
    }

    setShowSupportFallback(false);
    const timeoutId = window.setTimeout(() => {
      setShowSupportFallback(true);
    }, LOADING_SUPPORT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldShowLoadingState]);

  // Debug logging removed — was firing on every render, flooding console

  if (loading) {
    return <ProtectedRouteLoadingState showSupportFallback={showSupportFallback} />;
  }

  if (!user) {
    if (!authSettled) {
      return <ProtectedRouteLoadingState showSupportFallback={showSupportFallback} />;
    }
    if (import.meta.env.DEV) logger.log('[ProtectedRoute] NO USER - redirecionando para /auth');
    return <Navigate to="/auth" replace />;
  }

  // Admin-only route check
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  // Quiz exempt routes (including PWA onboarding flow, gift page)
  const quizExemptRoutes = [
    '/quiz',
    '/refund',
    '/refund-status',
    '/pwa-install',
    '/pwa-check',
    '/theme-selection',
    '/gift',
    // Core-promise page: must be reachable even before onboarding completes
    '/magic-words',
  ];

  const isQuizExempt = quizExemptRoutes.some(route => location.pathname.startsWith(route));

  // If quiz_completed is undefined, profile data is still loading.
  // Treat as loading state to prevent premature redirect to quiz.
  if (user.quiz_completed === undefined) {
    if (import.meta.env.DEV) {
      logger.log('[ProtectedRoute] quiz_completed undefined - aguardando dados do perfil');
    }
    return <ProtectedRouteLoadingState showSupportFallback={showSupportFallback} />;
  }

  // If quiz completed, allow access and ensure flags are set
  if (user.quiz_completed === true) {
    // Ensure localStorage flags are set for existing users
    if (!localStorage.getItem('onboarding_completed') && isStandaloneMode()) {
      localStorage.setItem('onboarding_completed', 'true');
    }
    return <>{children}</>;
  }

  // If not on exempt route and onboarding not completed, redirect to quiz
  if (!isQuizExempt) {
    if (import.meta.env.DEV) logger.log('[ProtectedRoute] Onboarding nao completado - redirecionando para /quiz');
    return <Navigate to="/quiz" replace />;
  }

  return <>{children}</>;
}
